/**
 * Opt-in error tracking. Sentry is loaded lazily and only when VITE_SENTRY_DSN
 * is configured at build time, so a deployment without a DSN ships zero Sentry
 * code in the main bundle and pays no runtime cost. To enable, set the env var
 * in the Cloudflare Pages build (and optionally VITE_SENTRY_ENV / VITE_RELEASE).
 */
export function initObservability(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  void import("@sentry/react")
    .then((Sentry) => {
      Sentry.init({
        dsn,
        environment: import.meta.env.VITE_SENTRY_ENV ?? "production",
        release: import.meta.env.VITE_RELEASE,
        // Conservative defaults, we capture errors, not full session replays.
        tracesSampleRate: 0,
        sendDefaultPii: false,
        integrations: [],
      });
    })
    .catch(() => {
      /* never let observability break the app */
    });
}

/**
 * Report a caught error. Always logs to the console; additionally forwards to
 * Sentry when a DSN is configured (the dynamic import is cached, so this reuses
 * the instance initialised by initObservability). Never throws.
 */
export function captureException(err: unknown, context?: Record<string, unknown>): void {
  try {
    console.error("[insnip] captured error", err, context ?? "");
  } catch {
    /* console may be unavailable */
  }
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  void import("@sentry/react")
    .then((Sentry) => Sentry.captureException(err, context ? { extra: context } : undefined))
    .catch(() => {
      /* never let observability break the app */
    });
}
